import React, { useState, useEffect } from 'react';
import { useParams, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useSetPageHeader } from '../context/PageHeaderContext';
import {
    PencilIcon,
    ShareIcon,
    ChartBarIcon,
    ClipboardDocumentListIcon,
    MapIcon,
    RectangleStackIcon,
    ArrowPathIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface Project {
    _id: string;
    name: string;
    description: string;
    createdAt: string;
    goals?: any[];
}

const ProjectView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<Project | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProject = async () => {
            if (!id) return;

            setIsLoading(true);
            setError(null);

            try {
                const response: any = await api.get(`/projects/${id}`);
                if (response.success) {
                    setProject(response.data);
                }
            } catch (err: any) {
                setError(err.message || 'Error loading project');
            } finally {
                setIsLoading(false);
            }
        };

        fetchProject();
    }, [id]);

    // Set page header
    useSetPageHeader(
        project?.name || 'Project',
        'Manage your project',
        <>
            <button
                onClick={() => navigate(`/projects/${id}/edit`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition"
            >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition">
                <ShareIcon className="h-4 w-4 mr-2" />
                Share
            </button>
        </>,
        <button
            onClick={() => navigate('/projects')}
            className="bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-shadow duration-200 hover:bg-gray-50"
            aria-label="Back to projects"
        >
            <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
        </button>,
        [project, id]
    );

    if (isLoading) {
        return (
            <div className="text-center py-16">
                <ArrowPathIcon className="mx-auto h-10 w-10 text-blue-400 animate-spin" />
                <p className="mt-4 text-base text-gray-500">Loading project...</p>
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="text-center py-16">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg
                        className="w-10 h-10 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Oops! Something went wrong
                </h2>
                <p className="text-gray-600 mb-6">{error || 'Project not found'}</p>
            </div>
        );
    }

    const navItems = [
        {
            name: 'Overview',
            path: `/projects/${id}`,
            icon: RectangleStackIcon,
            end: true,
        },
        {
            name: 'Feedback',
            path: `/projects/${id}/feedback`,
            icon: ClipboardDocumentListIcon,
        },
        {
            name: 'Tasks',
            path: `/projects/${id}/tasks`,
            icon: ChartBarIcon,
        },
        {
            name: 'Roadmap',
            path: `/projects/${id}/roadmaps`,
            icon: MapIcon,
        },
        {
            name: 'Analytics',
            path: `/projects/${id}/analytics`,
            icon: ChartBarIcon,
        },
    ];

    return (
        <div className="space-y-6">
            {/* Navigation Tabs */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <nav className="flex flex-wrap gap-2 p-2 sm:p-3" aria-label="Tabs">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.name}
                                to={item.path}
                                end={item.end}
                                className={({ isActive }) =>
                                    `flex items-center px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 ${isActive
                                        ? 'bg-accent-500 text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`
                                }
                            >
                                <Icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                                {item.name}
                            </NavLink>
                        );
                    })}
                </nav>
            </div>

            {/* Nested Route Content */}
            <div>
                <Outlet context={{ project }} />
            </div>
        </div>
    );
};

export default ProjectView;
